import React, { type FunctionComponent } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy, LazyWait } from '@atlassian/react-loosely-lazy';
import { useInvitePeopleModal } from '../controllers/index.tsx';
import type InvitePeopleModalType from './content/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncInvitePeopleModal = lazy<typeof InvitePeopleModalType>(
	() => import(/* webpackChunkName: "async-invite-people-modal" */ './content/index.tsx'),
);

interface InvitePeopleModalInternalProps {
	disableNoInviteError?: boolean;
}

const InvitePeopleModalInternal: FunctionComponent<InvitePeopleModalInternalProps> = ({
	disableNoInviteError,
}) => {
	const [{ isOpen, inviteFlow, continueUrl, source, subProduct }, { closeModal }] =
		useInvitePeopleModal();

	return (
		<LazyWait until={isOpen}>
			<Placeholder name="invite-people-modal" fallback={null}>
				<AsyncInvitePeopleModal
					inviteFlow={inviteFlow}
					isOpen={isOpen}
					onClose={closeModal}
					continueUrl={continueUrl}
					disableNoInviteError={disableNoInviteError}
					source={source}
					subProduct={subProduct}
				/>
			</Placeholder>
		</LazyWait>
	);
};

InvitePeopleModalInternal.displayName = 'InvitePeopleModalInternal';

interface InvitePeopleModalWithErrorBoundaryProps {
	disableNoInviteError?: boolean;
}

export const InvitePeopleModal: FunctionComponent<InvitePeopleModalWithErrorBoundaryProps> = ({
	disableNoInviteError,
}) => (
	<ErrorBoundary id="invite-people-modal" packageName="jiraInvitePeopleModal">
		<InvitePeopleModalInternal disableNoInviteError={disableNoInviteError} />
	</ErrorBoundary>
);
